/**
 * @file Toast notification component for user feedback using Bootstrap 5.3
 * @version 1.0.0
 * @author Matthieu Chareton
 * @since 2025-07-08
 */

import { useNotifications } from '../core/errors.js';

const { defineComponent, h, TransitionGroup, computed } = Vue;

export default defineComponent({
  name: 'ToastNotifications',

  props: {
    position: {
      type: String,
      default: 'top-right',
      validator: value =>
        [
          'top-left',
          'top-center',
          'top-right',
          'bottom-left',
          'bottom-center',
          'bottom-right',
        ].includes(value),
    },
    maxNotifications: {
      type: Number,
      default: 5,
    },
  },

  setup(props) {
    const { notifications, dismissNotification } = useNotifications();

    const getPositionClasses = () => {
      const positions = {
        'top-left': 'top-0 start-0',
        'top-center': 'top-0 start-50 translate-middle-x',
        'top-right': 'top-0 end-0',
        'bottom-left': 'bottom-0 start-0',
        'bottom-center': 'bottom-0 start-50 translate-middle-x',
        'bottom-right': 'bottom-0 end-0',
      };
      return positions[props.position];
    };

    const getToastClasses = type => {
      // Use subtle background for the entire toast
      return 'border';
    };

    const getToastHeaderClasses = type => {
      const typeClasses = {
        success: 'text-bg-success',
        error: 'text-bg-danger',
        warning: 'text-bg-warning',
        info: 'text-bg-info',
      };
      return typeClasses[type] || 'text-bg-info';
    };

    const getTypeIcon = type => {
      const icons = {
        success: 'bi-check-circle-fill',
        error: 'bi-exclamation-triangle-fill',
        warning: 'bi-exclamation-triangle-fill',
        info: 'bi-info-circle-fill',
      };
      return icons[type] || 'bi-info-circle-fill';
    };

    const displayedNotifications = computed(() => {
      return notifications.value.slice(-props.maxNotifications);
    });

    const createToast = notification => {
      return h(
        'div',
        {
          key: notification.id,
          class: `toast show ${getToastClasses(notification.type)}`,
          role: 'alert',
          'aria-live': 'assertive',
          'aria-atomic': 'true',
        },
        [
          h(
            'div',
            {
              class: `toast-header ${getToastHeaderClasses(notification.type)}`,
            },
            [
              h('i', { class: `${getTypeIcon(notification.type)} me-2` }),
              h(
                'strong',
                { class: 'me-auto' },
                notification.type.charAt(0).toUpperCase() +
                  notification.type.slice(1)
              ),
              h(
                'small',
                { class: 'text-opacity-75' },
                notification.timestamp.toLocaleTimeString()
              ),
              h('button', {
                type: 'button',
                class: 'btn-close',
                'aria-label': 'Close',
                onClick: () => dismissNotification(notification.id),
              }),
            ]
          ),
          h('div', { class: 'toast-body' }, [
            typeof notification.message === 'string'
              ? notification.message
              : h(notification.message),
          ]),
        ]
      );
    };

    return () => {
      if (displayedNotifications.value.length === 0) {
        return null;
      }

      return h(
        'div',
        {
          class: `toast-container position-fixed ${getPositionClasses()} p-3`,
          style: { zIndex: 1080 },
        },
        [displayedNotifications.value.map(createToast)]
      );
    };
  },
});
